import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Badge,
  Card,
  CardBody,
  Row,
  Col,
  Spinner,
  Input,
  FormGroup,
  Label,
} from "reactstrap";
import { getUserReports } from "./reportedUsersApi";
import UserInfoModal from "../../../components/Common/UserInfoModal";
import EditUserReportModal from "./editUserReportModal";
import BatchUpdateUserReportsModal from "./batchUpdateUserReportsModal";

const UserReportsModal = ({ isOpen, toggle, userId, username }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [stateFilter, setStateFilter] = useState("");
  const [userInfoModal, setUserInfoModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedReports, setSelectedReports] = useState([]);
  const [editModal, setEditModal] = useState(false);
  const [reportToEdit, setReportToEdit] = useState(null);
  const [batchUpdateModal, setBatchUpdateModal] = useState(false);
  const abortControllerRef = useRef(null);
  const isMountedRef = useRef(true);

  const limit = 50;

  const fetchReports = useCallback(async (page = 1, append = false) => {
    if (!userId) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setLoading(true);
    try {
      const result = await getUserReports({ userId, limit, page });
      
      if (abortController.signal.aborted || !isMountedRef.current) {
        return;
      }

      if (append) {
        setReports((prev) => [...prev, ...result.reports]);
      } else {
        setReports(result.reports);
      }

      setCurrentPage(result.pagination.currentPage);
      setTotalPages(result.pagination.totalPages);
      setTotalRecords(result.pagination.totalRecords);
      setHasMore(result.pagination.currentPage < result.pagination.totalPages);
    } catch (error) {
      if (error.name !== 'AbortError' && isMountedRef.current) {
        console.error("Error fetching user reports:", error);
      }
    } finally {
      if (isMountedRef.current && !abortController.signal.aborted) {
        setLoading(false);
      }
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
      }
    }
  }, [userId, limit]);

  useEffect(() => {
    isMountedRef.current = true;
    if (isOpen && userId) {
      setCurrentPage(1);
      setReports([]);
      fetchReports(1, false);
    }

    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [isOpen, userId, fetchReports]);

  // Refetch when filter changes
  useEffect(() => {
    if (isOpen && userId) {
      setCurrentPage(1);
      fetchReports(1, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateFilter]);

  const handleLoadMore = () => {
    if (hasMore && !loading) {
      fetchReports(currentPage + 1, true);
    }
  };

  // Filter reports based on state - moved before callbacks that use it
  const filteredReports = useMemo(() => {
    return reports.filter(report => {
      if (!stateFilter) return true;
      const reportState = report.state?.toLowerCase().replace(/_/g, '').replace(/\s/g, '');
      const filterState = stateFilter.toLowerCase().replace(/_/g, '').replace(/\s/g, '');
      return reportState === filterState;
    });
  }, [reports, stateFilter]);

  const handleViewUserInfo = useCallback((userId) => {
    if (!userId) return;
    setSelectedUserId(userId);
    setUserInfoModal(true);
  }, []);

  const handleEditReport = useCallback((report) => {
    setReportToEdit(report);
    setEditModal(true);
  }, []);

  const handleReportUpdate = useCallback(() => {
    // Refresh reports after update
    fetchReports(currentPage, false);
    setSelectedReports([]);
  }, [currentPage, fetchReports]);

  const handleToggleReportSelection = useCallback((report) => {
    setSelectedReports((prev) => {
      const isSelected = prev.some((r) => r.report_id === report.report_id);
      if (isSelected) {
        return prev.filter((r) => r.report_id !== report.report_id);
      } else {
        return [...prev, report];
      }
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedReports.length === filteredReports.length) {
      setSelectedReports([]);
    } else {
      setSelectedReports([...filteredReports]);
    }
  }, [selectedReports.length, filteredReports]);

  const handleBatchUpdate = useCallback(() => {
    if (selectedReports.length === 0) {
      alert("Please select at least one report to update.");
      return;
    }
    setBatchUpdateModal(true);
  }, [selectedReports]);

  const getStateBadge = (state) => {
    switch (state?.toLowerCase()) {
      case "resolved":
        return <Badge color="success" className="px-2 py-1">Resolved</Badge>;
      case "raised":
        return <Badge color="warning" className="px-2 py-1">Raised</Badge>;
      case "inprogress":
      case "in_progress":
      case "in progress":
        return <Badge color="info" className="px-2 py-1">In Progress</Badge>;
      case "none":
        return <Badge color="secondary" className="px-2 py-1">None</Badge>;
      default:
        return <Badge color="secondary" className="px-2 py-1">{state || "None"}</Badge>;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const handleClose = () => {
    setReports([]);
    setCurrentPage(1);
    setStateFilter("");
    setUserInfoModal(false);
    setSelectedUserId(null);
    setSelectedReports([]);
    setEditModal(false);
    setReportToEdit(null);
    setBatchUpdateModal(false);
    toggle();
  };

  return (
    <>
      <Modal isOpen={isOpen} toggle={handleClose} centered size="lg" className="user-reports-modal">
        <ModalHeader className="bg-danger text-white" toggle={handleClose}>
          <div className="d-flex align-items-center">
            <i className="mdi mdi-flag me-2"></i>
            <span>Reports for {username || `User ${userId}`}</span>
          </div>
        </ModalHeader>
        <ModalBody style={{ maxHeight: "70vh", overflowY: "auto" }} className="p-3">
          {loading && reports.length === 0 ? (
            <div className="text-center py-5">
              <Spinner color="danger" />
              <p className="mt-3 text-muted">Loading reports...</p>
            </div>
          ) : reports.length === 0 ? (
            <div className="text-center py-5">
              <i className="mdi mdi-information-outline" style={{ fontSize: "3rem", color: "#6c757d" }}></i>
              <p className="mt-3 text-muted">No reports found for this user.</p>
            </div>
          ) : (
            <>
              <div className="mb-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div>
                  <Badge color="info" className="px-3 py-2 me-2">
                    Total Reports: {totalRecords}
                  </Badge>
                  {filteredReports.length !== reports.length && (
                    <Badge color="secondary" className="px-3 py-2">
                      Filtered: {filteredReports.length}
                    </Badge>
                  )}
                  {selectedReports.length > 0 && (
                    <Badge color="primary" className="px-3 py-2">
                      Selected: {selectedReports.length}
                    </Badge>
                  )}
                </div>
                <div className="d-flex gap-2 align-items-center">
                  <FormGroup className="mb-0" style={{ minWidth: "150px" }}>
                    <Input
                      type="select"
                      value={stateFilter}
                      onChange={(e) => setStateFilter(e.target.value)}
                      size="sm"
                    >
                      <option value="">All States</option>
                      <option value="raised">Raised</option>
                      <option value="inProgress">In Progress</option>
                      <option value="resolved">Resolved</option>
                    </Input>
                  </FormGroup>
                  {filteredReports.length > 0 && (
                    <Button
                      color="secondary"
                      size="sm"
                      onClick={handleSelectAll}
                      className="px-3"
                    >
                      <i className="mdi mdi-checkbox-multiple-marked me-1"></i>
                      {selectedReports.length === filteredReports.length ? "Deselect All" : "Select All"}
                    </Button>
                  )}
                  {selectedReports.length > 0 && (
                    <Button
                      color="primary"
                      size="sm"
                      onClick={handleBatchUpdate}
                      className="px-3"
                    >
                      <i className="mdi mdi-pencil me-1"></i>
                      Batch Update ({selectedReports.length})
                    </Button>
                  )}
                </div>
              </div>
              {filteredReports.length === 0 ? (
                <div className="text-center py-4">
                  <i className="mdi mdi-filter-remove" style={{ fontSize: "2rem", color: "#6c757d" }}></i>
                  <p className="mt-2 text-muted">No reports match the selected filter</p>
                </div>
              ) : (
                filteredReports.map((report, index) => {
                  const isSelected = selectedReports.some((r) => r.report_id === report.report_id);
                  return (
                    <Card 
                      key={index} 
                      className={`mb-3 shadow-sm border-left-danger ${isSelected ? 'border-primary' : ''}`}
                      style={{ borderLeftWidth: "4px" }}
                    >
                      <CardBody className="p-3">
                        <Row className="g-2">
                          <Col xs={12}>
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <div className="d-flex align-items-center gap-2">
                                <Input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => handleToggleReportSelection(report)}
                                  className="me-2"
                                />
                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                  <Badge color="danger" className="px-2 py-1 me-2">
                                    <i className="mdi mdi-tag me-1"></i>{report.category}
                                  </Badge>
                                  {getStateBadge(report.state)}
                                  {report.reportType && (
                                    <Badge color="info" className="px-2 py-1">
                                      <i className="mdi mdi-file-document me-1"></i>{report.reportType.name}
                                    </Badge>
                                  )}
                                  {report.report_id && (
                                    <Badge color="secondary" className="px-2 py-1 font-monospace small">
                                      ID: {report.report_id.substring(0, 8)}...
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              <div className="d-flex align-items-center gap-2">
                                <div className="text-muted small">
                                  <i className="mdi mdi-clock-outline me-1"></i>
                                  {formatDate(report.createdAt)}
                                </div>
                                <Button
                                  color="info"
                                  size="sm"
                                  onClick={() => handleEditReport(report)}
                                  className="px-2"
                                  title="Edit Report"
                                >
                                  <i className="mdi mdi-pencil"></i>
                                </Button>
                              </div>
                          </div>
                        </Col>

                        <Col md={6}>
                          <div className="mb-2">
                            <label className="form-label text-muted small mb-1">
                              <i className="mdi mdi-account-alert me-1"></i>Reporter
                            </label>
                            <div className="d-flex align-items-center gap-2">
                              <div className="flex-grow-1">
                                {report.reporter && report.reporter.userId ? (
                                  <div className="text-muted small font-monospace">{report.reporter.userId}</div>
                                ) : (
                                  <div className="text-muted small">Anonymous</div>
                                )}
                              </div>
                              {report.reporter && report.reporter.userId && (
                                <Button
                                  color="primary"
                                  size="sm"
                                  onClick={() => handleViewUserInfo(report.reporter.userId)}
                                  className="px-2"
                                  title="View Reporter Details"
                                >
                                  <i className="mdi mdi-eye"></i>
                                </Button>
                              )}
                            </div>
                          </div>
                        </Col>

                        {report.reportedUser && report.reportedUser.userId && (
                          <Col md={6}>
                            <div className="mb-2">
                              <label className="form-label text-muted small mb-1">
                                <i className="mdi mdi-account me-1"></i>Reported User
                              </label>
                              <div className="d-flex align-items-center gap-2">
                                <div className="flex-grow-1">
                                  <div className="text-muted small font-monospace">{report.reportedUser.userId}</div>
                                </div>
                                <Button
                                  color="primary"
                                  size="sm"
                                  onClick={() => handleViewUserInfo(report.reportedUser.userId)}
                                  className="px-2"
                                  title="View Reported User Details"
                                >
                                  <i className="mdi mdi-eye"></i>
                                </Button>
                              </div>
                            </div>
                          </Col>
                        )}

                        {report.reason && (
                          <Col xs={12}>
                            <div className="mb-2">
                              <label className="form-label text-muted small mb-1 fw-bold">
                                <i className="mdi mdi-alert-circle me-1"></i>Reason
                              </label>
                              <div className="p-2 bg-warning-subtle rounded border border-warning">
                                {report.reason}
                              </div>
                            </div>
                          </Col>
                        )}

                        {report.adminNote && (
                          <Col xs={12}>
                            <div className="mb-2">
                              <label className="form-label text-muted small mb-1 fw-bold">
                                <i className="mdi mdi-note-text me-1"></i>Admin Note
                              </label>
                              <div className="p-2 bg-info-subtle rounded border border-info">
                                {report.adminNote}
                              </div>
                            </div>
                          </Col>
                        )}
                      </Row>
                    </CardBody>
                  </Card>
                  );
                })
              )}

              {hasMore && (
                <div className="text-center mt-3">
                  <Button
                    color="primary"
                    outline
                    onClick={handleLoadMore}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Loading...
                      </>
                    ) : (
                      <>
                        <i className="mdi mdi-arrow-down me-2"></i>
                        Load More ({totalRecords - reports.length} remaining)
                      </>
                    )}
                  </Button>
                </div>
              )}

              {!hasMore && reports.length > 0 && (
                <div className="text-center mt-3">
                  <Badge color="secondary" className="px-3 py-2">
                    All reports loaded
                  </Badge>
                </div>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter className="bg-light">
          {selectedReports.length > 0 && (
            <Button
              color="primary"
              onClick={handleBatchUpdate}
              className="px-4 me-2"
            >
              <i className="mdi mdi-pencil me-2"></i>Batch Update ({selectedReports.length})
            </Button>
          )}
          <Button color="secondary" onClick={handleClose} className="px-4">
            <i className="mdi mdi-close me-2"></i>Close
          </Button>
        </ModalFooter>
      </Modal>

      <EditUserReportModal
        isOpen={editModal}
        toggle={() => {
          setEditModal(false);
          setReportToEdit(null);
        }}
        report={reportToEdit}
        onUpdate={handleReportUpdate}
      />

      <BatchUpdateUserReportsModal
        isOpen={batchUpdateModal}
        toggle={() => {
          setBatchUpdateModal(false);
        }}
        selectedReports={selectedReports}
        onUpdate={handleReportUpdate}
      />

      <UserInfoModal
        isOpen={userInfoModal}
        toggle={() => {
          setUserInfoModal(false);
          setSelectedUserId(null);
        }}
        userId={selectedUserId}
        onUpdate={() => {
          // Optionally refresh reports after user action
          if (userId) {
            fetchReports(currentPage, false);
          }
        }}
      />
    </>
  );
};

export default UserReportsModal;

